import { DeleteOutlined } from '@ant-design/icons';
import { Input, Popconfirm } from 'antd';
import { useContext } from 'react';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import { MarkerItem } from '../../../../types/home';
import { withLoading } from '../../../common/Loading/withLoading';
import { useCoordinatesManagement } from '../hooks/useCoordinateManagement';
import styles from '../Style/ResultPanel.module.scss';
import { POINT_NAME_MAX_LENGTH } from '../utils/pointNameValidation';
import { CoordinatesInfo } from './CoordinatesInfo';

interface PointActionsProps {
  id: string;
  index: number;
  markerPoint: MarkerItem | undefined;
  editable: boolean;
  addCoords: (coords: number[]) => void;
  pointIsInCard: boolean;
  onChangePointLocation: (
    coords: number[],
    forceChange?: boolean | undefined
  ) => Promise<void>;
  resetCoordsKey: string;
  onPointNameChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => void;
  onPointsDelete: (id: string) => void;
  onMark: (bool: boolean) => void;
  validateInput: (event: React.ChangeEvent<HTMLInputElement>) => boolean;
  isAddMarkerMode: boolean;
}

export const PointActions = ({
  id,
  index,
  markerPoint,
  editable,
  addCoords,
  pointIsInCard,
  onChangePointLocation,
  resetCoordsKey,
  onPointNameChange,
  onPointsDelete,
  onMark,
  isAddMarkerMode,
  validateInput,
}: PointActionsProps) => {
  const { UTMData, showUTM, setShowUTM, handleChangeUTMData } =
    useCoordinatesManagement();

  const loadingContext = useContext(LoadingContext);

  if (markerPoint === undefined) {
    return (
      <div>
        <CoordinatesInfo
          key={`empty-${index}`}
          index={index}
          id={id}
          name={''}
          x={null}
          y={null}
          changeCoords={onChangePointLocation}
          addCoords={addCoords}
          disableCoordinateChange={pointIsInCard}
          resetCoordsKey={resetCoordsKey}
          UTMItem={UTMData[id]}
          setUTMItem={(item) => handleChangeUTMData(id, item)}
          showUTM={showUTM}
          setShowUTM={setShowUTM}
          onPointNameChange={onPointNameChange}
          onPointsDelete={onPointsDelete}
          markerPoint={markerPoint}
          onMark={onMark}
        />
        <div>Click on the map or type in coordinates.</div>
      </div>
    );
  }

  if (editable && markerPoint.active && !isAddMarkerMode) {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className={styles.activePointContainer}
      >
        <CoordinatesInfo
          key={`active-${id}`}
          id={id}
          index={index}
          name={markerPoint.label}
          x={
            markerPoint.coordinates.length > 1
              ? markerPoint.coordinates[0]
              : null
          }
          y={
            markerPoint.coordinates.length > 1
              ? markerPoint.coordinates[1]
              : null
          }
          changeCoords={onChangePointLocation}
          addCoords={addCoords}
          disableCoordinateChange={pointIsInCard}
          resetCoordsKey={resetCoordsKey}
          UTMItem={UTMData[id]}
          setUTMItem={(item) => handleChangeUTMData(id, item)}
          showUTM={showUTM}
          setShowUTM={setShowUTM}
          onPointNameChange={onPointNameChange}
          onPointsDelete={onPointsDelete}
          markerPoint={markerPoint}
        />
      </div>
    );
  }

  if (editable) {
    return (
      <div className={`${styles.pointActions} pointActions`}>
        <Input
          id={`PointPanel-Name_Input_${index}`}
          size='small'
          value={markerPoint.label}
          maxLength={POINT_NAME_MAX_LENGTH}
          onChange={(e) =>
            validateInput(e) && onPointNameChange(e, markerPoint.id)
          }
          style={{ width: '25%' }}
        />

        {!showUTM ? (
          <div style={{ marginLeft: '10px', display: 'flex' }}>
            <Input
              prefix={'Long: '}
              size='small'
              value={`${markerPoint.coordinates[0]}`}
              maxLength={10}
              disabled
              style={{ margin: '0 5px' }}
            />
            <Input
              prefix={'Lat: '}
              size='small'
              value={`${markerPoint.coordinates[1]}`}
              maxLength={10}
              disabled
              style={{ margin: '0 5px' }}
            />
          </div>
        ) : (
          <div style={{ marginLeft: '10px', display: 'flex' }}>
            <Input
              prefix={'East: '}
              size='small'
              value={`${UTMData[id].east}`}
              maxLength={10}
              disabled
              style={{ margin: '0 2px' }}
            />
            <Input
              prefix={'North: '}
              size='small'
              value={`${UTMData[id].north}`}
              maxLength={10}
              disabled
              style={{ margin: '0 2px' }}
            />
            <Input
              prefix={'UTM Zone: '}
              size='small'
              value={`${UTMData[id].zone}`}
              maxLength={10}
              disabled
              style={{ margin: '0 2px' }}
            />
          </div>
        )}

        <Popconfirm
          placement='left'
          title={'Are you sure to delete this Point?'}
          onConfirm={() =>
            withLoading(() => onPointsDelete(markerPoint.id), loadingContext, {
              source: 'onPointsDelete',
            })
          }
          okText='Yes'
          cancelText='No'
        >
          <span onClick={(e) => e.stopPropagation()}>
            <DeleteOutlined className={styles.deleteIcon} />
          </span>
        </Popconfirm>
      </div>
    );
  }

  return (
    <CoordinatesInfo
      key={`readonly-${markerPoint.id}`}
      id={id}
      index={index}
      name={markerPoint.label}
      x={markerPoint.coordinates.length > 1 ? markerPoint.coordinates[0] : null}
      y={markerPoint.coordinates.length > 1 ? markerPoint.coordinates[1] : null}
      changeCoords={onChangePointLocation}
      addCoords={addCoords}
      disableCoordinateChange={pointIsInCard}
      resetCoordsKey={resetCoordsKey}
      UTMItem={UTMData[id]}
      setUTMItem={(item) => handleChangeUTMData(id, item)}
      showUTM={showUTM}
      setShowUTM={setShowUTM}
      onPointNameChange={onPointNameChange}
      onPointsDelete={onPointsDelete}
      markerPoint={markerPoint}
    />
  );
};
